import { Hero } from "@/.storyblok/types/319751/storyblok-components";
import { BASE_PATH } from "./storyblok-client";
import { getStory } from "./storyblok-api";
import { logger } from '@/lib/logger';
import { cache } from 'react';

/**
 * Helper to fetch common layout blocks in parallel.
 */
export const fetchLayoutBlocks = cache(async () => {
  const layoutBlocks = await Promise.all([
    getStory(`${BASE_PATH}/globals/header`),
    getStory(`${BASE_PATH}/globals/footer`),
    getStory(`${BASE_PATH}/globals/accreditation-small`),
    getStory(`${BASE_PATH}/globals/stickynav`),
    getStory(`${BASE_PATH}/globals/custom-script`),
  ]);

  return {
    headerBlock: layoutBlocks[0]?.content || null,
    footerBlock: layoutBlocks[1]?.content || null,
    trustSmallBlock: layoutBlocks[2]?.content || null,
    stickyNavBlock: layoutBlocks[3]?.content || null,
    customScriptBlock: layoutBlocks[4]?.content || null,
  };
});

export async function getLayoutContent() {
  try {
    const homeStory = await getStory(
      BASE_PATH,
      [
        "reference_selector.select_story",
        "blog_selector.select_story",
        "related_resources.select_story",
        "top_brands_section.testimonials"
      ],
      null
    );
    const layoutBlocks = await fetchLayoutBlocks();

    const content = homeStory?.content;
    const heroBlock =
      content?.body?.find(
        (block: Hero) => block.component === "hero"
      ) || null;
    // logger.log(`homeStory: ` + JSON.stringify(homeStory, null, 2));

    return {
      homeDataStory: homeStory,
      headerBlock: layoutBlocks.headerBlock,
      footerBlock: layoutBlocks.footerBlock,
      trustSmallBlock: layoutBlocks.trustSmallBlock,
      customScriptBlock: layoutBlocks.customScriptBlock,
      stickyNavBlock: layoutBlocks.stickyNavBlock,
      heroBlock,
    };
  } catch (error) {
    logger.error("Error fetching homepage layout:", error);
    return {
      homeDataStory: null,
      headerBlock: null,
      footerBlock: null,
      trustSmallBlock: null,
      trustSmallHeaderBlock: null,
      customScriptBlock: null,
      stickyNavBlock: null,
      heroBlock: null,
    };
  }
}

export async function getDynamicPageWithLayout(slug: string) {
  try {
    const dynamicStory = await getStory(
      `${BASE_PATH}/${slug}`,
      [
        "reference_selector.select_story",
        "meet_pen_test.blog_author",
        "blog_selector.select_story",
        "related_resources.select_story",
        "top_brands_section.testimonials",
        "download_card_industry_reports.items"
      ],
      null
    );

    // logger.log(`dynamicStory: ` + JSON.stringify(dynamicStory, null, 2));

    const { headerBlock, footerBlock, trustSmallBlock, stickyNavBlock, customScriptBlock } =
      await fetchLayoutBlocks();

    const content = dynamicStory?.content;
    const heroBlock =
      content?.body?.find(
        (block: Hero) => block.component === "hero"
      ) || null;

    return {
      dynamicPageStory: dynamicStory,
      headerBlock,
      footerBlock,
      trustSmallBlock,
      customScriptBlock,
      stickyNavBlock,
      heroBlock,
    };
  } catch (error) {
    if (error instanceof Error && error.message === "STORY_NOT_FOUND") {
      throw error;
    }

    logger.error(`Error fetching dynamic page content (${slug}):`, error);
    return {
      dynamicPageStory: null,
      headerBlock: null,
      footerBlock: null,
      trustSmallBlock: null,
      customScriptBlock: null,
      stickyNavBlock: null,
      heroBlock: null,
    };
  }
}
